'use client';

import React from 'react';

interface ConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  title: string;
  message: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'danger' | 'warning' | 'info' | 'active';
  loading?: boolean;
}

const variantConfig = {
  danger: {
    icon: 'ri-delete-bin-line',
    iconBg: 'bg-[#FFD6D6] dark:bg-[var(--hrm-primary-dark)]/40',
    iconColor: 'text-[var(--hrm-primary-dark)] dark:text-[var(--hrm-primary)]',
    buttonBg: 'bg-[var(--hrm-primary-dark)] hover:bg-[var(--hrm-primary-dark)] focus:ring-[var(--hrm-primary)]',
    buttonDisabledBg: 'bg-[var(--hrm-primary)] cursor-not-allowed',
  },
  warning: {
    icon: 'ri-error-warning-line',
    iconBg: 'bg-amber-100 dark:bg-amber-900/40',
    iconColor: 'text-amber-600 dark:text-amber-400',
    buttonBg: 'bg-[var(--hrm-primary)] hover:bg-amber-700 focus:ring-amber-500',
    buttonDisabledBg: 'bg-amber-400 cursor-not-allowed',
  },
  info: {
    icon: 'ri-information-line',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    iconColor: 'text-blue-600 dark:text-blue-400',
    buttonBg: 'bg-[var(--hrm-primary)] hover:bg-[var(--hrm-primary-dark)] focus:ring-blue-500',
    buttonDisabledBg: 'bg-blue-400 cursor-not-allowed',
  },
  active: {
    icon: 'ri-information-line',
    iconBg: 'bg-blue-100 dark:bg-blue-900/40',
    iconColor: 'text-blue-600 dark:text-blue-400',
    buttonBg: 'bg-[var(--hrm-primary-dark)] hover:bg-[var(--hrm-primary-dark)]  focus:ring-[#FF0012]',
    buttonDisabledBg: 'bg-blue-400 cursor-not-allowed',
  },
} as const;

function Spinner() {
  return (
    <svg
      className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle
        className="opacity-25"
        cx="12"
        cy="12"
        r="10"
        stroke="currentColor"
        strokeWidth="4"
      />
      <path
        className="opacity-75"
        fill="currentColor"
        d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
      />
    </svg>
  );
}

const ConfirmDialog = React.memo(function ConfirmDialog({
  open,
  onClose,
  onConfirm,
  title,
  message,
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  variant = 'danger',
  loading = false,
}: ConfirmDialogProps) {
  // Close on Escape key
  React.useEffect(() => {
    if (!open) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [open, onClose]);

  if (!open) return null;

  const config = variantConfig[variant];

  return (
    <div className="fixed inset-0 z-[1100] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-200"
        onClick={onClose}
      />

      {/* Dialog */}
      <div
        className="
          relative w-full max-w-md bg-white dark:bg-slate-800 rounded-2xl shadow-2xl
          p-6 transform transition-all duration-200 animate-in fade-in zoom-in-95
        "
        role="dialog"
        aria-modal="true"
        aria-labelledby="confirm-dialog-title"
      >
        {/* Icon */}
        <div className="flex justify-center mb-4">
          <div className={`flex items-center justify-center w-14 h-14 rounded-full ${config.iconBg}`}>
            <i className={`${config.icon} text-2xl ${config.iconColor}`} />
          </div>
        </div>

        {/* Content */}
        <h3
          id="confirm-dialog-title"
          className="text-lg font-bold text-slate-900 dark:text-white text-center mb-2"
        >
          {title}
        </h3>
        <p className="text-sm text-slate-500 dark:text-slate-400 text-center leading-relaxed mb-6">
          {message}
        </p>

        {/* Actions */}
        <div className="flex gap-3">
          <button
            onClick={onClose}
            disabled={loading}
            className={`
              flex-1 px-4 py-2.5 text-sm font-semibold rounded-xl border transition-all duration-200
              bg-white dark:bg-slate-700
              border-slate-200 dark:border-slate-600
              text-slate-700 dark:text-slate-200
              hover:bg-slate-50 dark:hover:bg-[var(--hrm-primary)]
              focus:outline-none focus:ring-2 focus:ring-slate-300 dark:focus:ring-slate-500
              disabled:opacity-50 disabled:cursor-not-allowed
            `}
          >
            {cancelText}
          </button>
          <button
            onClick={onConfirm}
            disabled={loading}
            className={`
              flex-1 flex items-center justify-center px-4 py-2.5 text-sm font-semibold rounded-xl
              text-white transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-offset-2
              dark:focus:ring-offset-slate-800
              ${loading ? config.buttonDisabledBg : config.buttonBg}
            `}
          >
            {loading && <Spinner />}
            {confirmText}
          </button>
        </div>
      </div>
    </div>
  );
});

export default ConfirmDialog;
